import React, { createContext, useState, useEffect, useContext } from 'react';
import {
    login as authLogin,
    logout as authLogout,
    register as authRegister,
    getCurrentUser,
    getProfile,
    isAuthenticated as checkAuth,
    hasRole as checkRole
} from '../services/authService';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Check for existing user on first load
        const initAuth = async () => {
            try {
                if (checkAuth()) {
                    const savedUser = getCurrentUser();
                    if (savedUser) {
                        setUser(savedUser);
                    } else {
                        // If token exists but user data doesn't, fetch user data
                        const profile = await getProfile();
                        setUser(profile);
                    }
                }
            } catch (err) {
                console.error("Error initializing auth:", err);
                authLogout();
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    // Login function
    const login = async (credentials) => {
        try {
            setLoading(true);
            setError(null);
            const data = await authLogin(credentials);
            setUser(data);
            return data;
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to login');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Register function (Super Admin only)
    const register = async (userData) => {
        try {
            setLoading(true);
            setError(null);
            const data = await authRegister(userData);
            return data;
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to register user');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Logout function
    const logout = () => {
        authLogout();
        setUser(null);
    };

    // Check if user is authenticated
    const isAuthenticated = () => {
        return !!user && checkAuth();
    };

    // Check if user has a specific role
    const hasRole = (role) => {
        if (!user) return false;
        return checkRole(role);
    };

    const value = {
        user,
        loading,
        error,
        login,
        logout,
        register,
        isAuthenticated,
        hasRole
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};